import React, { useState } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
} from '@mui/material';
import { 
  Refresh, 
  Search, 
  FilterList, 
  KeyboardArrowDown, 
  KeyboardArrowUp,
  CreditCard,
  Edit,
  Delete,
  Add,
  Close,
  AttachMoney,
  AccountBalanceWallet,
  TrendingUp,
  Person,
  Email,
  Phone,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListCreditUsersQuery,
  useSetCreditMutation,
  useUpdateCreditMutation,
  useDeleteCreditMutation,
  CreditUser,
  SetCreditRequest,
  UpdateCreditRequest,
} from '../../features/admin/setCreditApi.ts';

interface CreditFormData {
  user_id: number | '';
  credit_limit: number | '';
  balance: number | '';
}

interface CreditFormErrors {
  user_id?: string;
  credit_limit?: string;
  balance?: string;
}

const SetCredit = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCredit, setEditingCredit] = useState<CreditUser | null>(null);
  const [formData, setFormData] = useState<CreditFormData>({
    user_id: '',
    credit_limit: '',
    balance: '',
  });
  const [formErrors, setFormErrors] = useState<CreditFormErrors>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  // API hooks
  const { 
    data: creditUsersResponse, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListCreditUsersQuery();

  const [setCredit] = useSetCreditMutation();
  const [updateCredit] = useUpdateCreditMutation();
  const [deleteCredit, { isLoading: isDeleting }] = useDeleteCreditMutation();

  // Handle API response structure - updated to match the image data
  const creditUsers = creditUsersResponse?.data || creditUsersResponse?.result || [];
  
  const usersPerPage = 10;
  const totalRecords = creditUsers.length;
  const totalPages = Math.ceil(totalRecords / usersPerPage);

  // Helper functions
  const getUserInitials = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
    const first = firstName?.trim() || '';
    const last = lastName?.trim() || '';
    
    if (!first && !last) return '?';
    if (!first) return last.charAt(0).toUpperCase();
    if (!last) return first.charAt(0).toUpperCase();
    
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  };

  const getUserFullName = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
    const first = firstName?.trim() || '';
    const last = lastName?.trim() || '';
    
    if (!first && !last) return 'Unknown User';
    if (!first) return last;
    if (!last) return first;
    
    return `${first} ${last}`;
  };

  const formatCurrency = (amount: string | number): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `₹${(num || 0).toFixed(2)}`;
  };

  const calculateUtilization = (creditLimit: string | number, balance: string | number): number => {
    const limit = typeof creditLimit === 'string' ? parseFloat(creditLimit) : creditLimit;
    const bal = typeof balance === 'string' ? parseFloat(balance) : balance;
    if (limit === 0) return 0;
    return Math.round(((limit - bal) / limit) * 100);
  };

  const getUtilizationColor = (percentage: number) => {
    if (percentage >= 80) return { bgcolor: '#ffebee', color: '#c62828' };
    if (percentage >= 50) return { bgcolor: '#fff3e0', color: '#e65100' };
    return { bgcolor: '#d1f4e0', color: '#00783e' };
  };

  // Get user email - handle both API response structures
  const getUserEmail = (user: any): string => {
    return user.user_email || user.email || '';
  };

  // Get user contact - handle both API response structures
  const getUserContact = (user: any): string => {
    return user.user_contact || user.user_cortact || user.phone || '';
  };

  // Get credit limit - handle both API response structures
  const getCreditLimit = (user: any): string => {
    return user.credit_list || user.credit_limit || '0';
  };

  // Get balance - handle both API response structures
  const getBalance = (user: any): string => {
    return user.balance || '0';
  };

  // Filter users based on search - UPDATED to search email and contact
  const filteredUsers = creditUsers.filter((user: any) => {
    if (!searchStr) return true;
    
    const fullName = getUserFullName(user.first_name, user.last_name);
    const email = getUserEmail(user).toLowerCase();
    const contact = getUserContact(user);
    const searchLower = searchStr.toLowerCase();
    
    return fullName.toLowerCase().includes(searchLower) ||
           email.includes(searchLower) ||
           contact.includes(searchStr) ||
           user.user_id.toString().includes(searchStr);
  });

  // Paginate users
  const paginatedUsers = filteredUsers.slice(
    (page - 1) * usersPerPage,
    page * usersPerPage
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      setSnackbar({
        open: true,
        message: 'Credit users refreshed successfully!',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Error refreshing credit users',
        severity: 'error'
      });
    }
  };

  const handleToggleExpand = (userId: number) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const clearFilters = () => {
    setSearchStr('');
    setPage(1);
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      credit_limit: '',
      balance: '',
    });
    setFormErrors({});
    setEditingCredit(null);
  };

  const handleAddCredit = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleEditCredit = (creditUser: any) => {
    setFormData({
      user_id: creditUser.user_id,
      credit_limit: parseFloat(getCreditLimit(creditUser)) || 0,
      balance: parseFloat(getBalance(creditUser)) || 0,
    });
    setEditingCredit(creditUser);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    let processedValue: string | number = value;
    if (name === 'user_id' || name === 'credit_limit' || name === 'balance') {
      processedValue = value === '' ? '' : Number(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));

    if (formErrors[name as keyof CreditFormErrors]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-set balance equal to credit limit when credit limit changes in add mode
    if (name === 'credit_limit' && !editingCredit && value !== '') {
      setFormData(prev => ({ ...prev, balance: processedValue }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: Number(value) }));
    
    if (formErrors[name as keyof CreditFormErrors]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: CreditFormErrors = {};

    if (!formData.user_id || formData.user_id === '') {
      newErrors.user_id = 'User selection is required';
    }

    if (!formData.credit_limit || formData.credit_limit <= 0) {
      newErrors.credit_limit = 'Credit limit must be greater than 0';
    }

    if (!formData.balance || formData.balance < 0) {
      newErrors.balance = 'Balance cannot be negative';
    }

    if (formData.balance && formData.credit_limit && Number(formData.balance) > Number(formData.credit_limit)) {
      newErrors.balance = 'Balance cannot exceed credit limit';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsFormSubmitting(true);

    try {
      let result;
      if (editingCredit) {
        const requestData: UpdateCreditRequest = {
          user_id: Number(formData.user_id),
          credit_limit: Number(formData.credit_limit),
          balance: Number(formData.balance)
        };
        
        result = await updateCredit(requestData).unwrap();
      } else {
        const requestData: SetCreditRequest = {
          user_id: Number(formData.user_id),
          credit_limit: Number(formData.credit_limit),
          balance: Number(formData.balance)
        };
        
        result = await setCredit(requestData).unwrap();
      }

      setSnackbar({
        open: true,
        message: result.message || `Credit ${editingCredit ? 'updated' : 'set'} successfully!`,
        severity: 'success'
      });

      handleFormClose();
      refetch();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.data?.message || error.message || `Error ${editingCredit ? 'updating' : 'setting'} credit.`,
        severity: 'error'
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleDelete = async (creditUser: any) => {
    const userName = getUserFullName(creditUser.first_name, creditUser.last_name);
    
    if (window.confirm(`Are you sure you want to remove credit facility for ${userName}?`)) {
      try {
        setDeletingUserId(creditUser.user_id);
        
        const result = await deleteCredit({ user_id: creditUser.user_id }).unwrap();

        setSnackbar({
          open: true,
          message: result.message || 'Credit facility removed successfully!',
          severity: 'success'
        });
        
        await refetch();
        
      } catch (err: any) {
        const errorMessage = err?.data?.message || err?.message || 'Error removing credit facility.';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      } finally {
        setDeletingUserId(null);
      }
    }
  };

  // Sample users for dropdown (you might want to fetch this from another API)
  const sampleUsers = [
    { user_id: 13, name: 'Sara Bala', email: 'balaksara@gmail3.com' },
    { user_id: 14, name: 'Saravanan', email: 'saravanan@example.com' },
  ];

  const availableUsers = sampleUsers.filter(user => 
    !creditUsers.some((creditUser: any) => creditUser.user_id === user.user_id)
  );

  // Calculate stats - UPDATED to use getter functions
  const totalBalance = creditUsers.reduce((sum: number, user: any) => 
    sum + (parseFloat(getBalance(user)) || 0), 0
  );
  
  const totalUtilized = creditUsers.reduce((sum: number, user: any) => 
    sum + ((parseFloat(getCreditLimit(user)) || 0) - (parseFloat(getBalance(user)) || 0)), 0
  );

  const totalCreditLimit = creditUsers.reduce((sum: number, user: any) => 
    sum + (parseFloat(getCreditLimit(user)) || 0), 0
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar for desktop */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        {/* Main content area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                  Credit Sales Management
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddCredit}
                    sx={{
                      textTransform: 'none',
                      bgcolor: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: '#000000',
                      }
                    }}
                  >
                    Add Credit
                  </Button>
                </Box>
              </Box>

              {/* Stats Cards */}
              {!isLoading && !isError && creditUsers.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ 
                            bgcolor: '#e3f2fd', 
                            borderRadius: '8px', 
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Person sx={{ color: '#1565c0', fontSize: 24 }} />
                          </Box>
                          <Box>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                              {creditUsers.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Credit Users
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ 
                            bgcolor: '#f3e5f5', 
                            borderRadius: '8px', 
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <AccountBalanceWallet sx={{ color: '#7b1fa2', fontSize: 24 }} />
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223', fontSize: '18px' }}>
                              {formatCurrency(totalCreditLimit)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Credit Limit
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ 
                            bgcolor: '#e8f5e9', 
                            borderRadius: '8px', 
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <AttachMoney sx={{ color: '#2e7d32', fontSize: 24 }} />
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223', fontSize: '18px' }}>
                              {formatCurrency(totalBalance)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Available Balance
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ 
                            bgcolor: '#fff3e0', 
                            borderRadius: '8px', 
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <TrendingUp sx={{ color: '#e65100', fontSize: 24 }} />
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223', fontSize: '18px' }}>
                              {formatCurrency(totalUtilized)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Utilized
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              )}

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={(e, v) => setTabValue(v)}
                    sx={{
                      px: 2,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '14px',
                        minHeight: 48,
                        color: '#6d7175',
                      },
                      '& .Mui-selected': {
                        color: '#202223',
                        fontWeight: 600,
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#202223',
                        height: 3,
                      }
                    }}
                  >
                    <Tab label={`All Users (${totalRecords})`} />
                  </Tabs>
                </Box>

                <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search by name, email, contact, or ID..."
                    value={searchStr}
                    onChange={handleSearch}
                    fullWidth
                    sx={{ 
                      maxWidth: 400,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        bgcolor: 'white',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    onClick={clearFilters}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Clear
                  </Button>
                </Box>

                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {isError && !isLoading && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">Failed to load credit users. Please try again.</Alert>
                  </Box>
                )}

                {!isLoading && !isError && paginatedUsers.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <CreditCard sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No credit users found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {searchStr ? `No results for "${searchStr}"` : 'Get started by adding credit facility for users'}
                    </Typography>
                    {!searchStr && (
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddCredit}
                        sx={{
                          textTransform: 'none',
                          bgcolor: '#202223',
                          borderRadius: '8px',
                          fontWeight: 500,
                        }}
                      >
                        Add First Credit Facility
                      </Button>
                    )}
                  </Box>
                )}

                {!isLoading && paginatedUsers.length > 0 && (
                  <Box sx={{ bgcolor: 'white' }}>
                    {/* Table Header - Desktop */}
                    {!isMobile && (
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '50px 80px 1fr 180px 140px 140px 120px 100px',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb'
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          ID
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          User
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Contact
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Credit Limit
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Available
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Utilized
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Actions
                        </Typography>
                      </Box>
                    )}

                    {/* Users List */}
                    {paginatedUsers.map((user: any) => {
                      const userInitials = getUserInitials(user.first_name, user.last_name);
                      const userFullName = getUserFullName(user.first_name, user.last_name);
                      const userEmail = getUserEmail(user);
                      const userContact = getUserContact(user);
                      const creditLimit = getCreditLimit(user);
                      const balance = getBalance(user);
                      const utilization = calculateUtilization(creditLimit, balance);
                      const isThisDeleting = deletingUserId === user.user_id;

                      return (
                        <Box key={user.user_id}>
                          {/* User Row */}
                          <Box
                            sx={!isMobile ? {
                              display: 'grid',
                              gridTemplateColumns: '50px 80px 1fr 180px 140px 140px 120px 100px',
                              gap: 2,
                              px: 2,
                              py: 2,
                              alignItems: 'center',
                              borderBottom: '1px solid #e1e3e5',
                              '&:hover': { bgcolor: '#f9fafb' },
                              transition: 'background-color 0.15s ease',
                              cursor: 'pointer'
                            } : {
                              p: 2,
                              borderBottom: '1px solid #e1e3e5',
                              '&:hover': { bgcolor: '#f9fafb' },
                              cursor: 'pointer'
                            }}
                            onClick={() => handleToggleExpand(user.user_id)}
                          >
                            {!isMobile ? (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleExpand(user.user_id);
                                  }}
                                  sx={{ color: '#6d7175' }}
                                >
                                  {expandedUserId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>

                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                  #{user.user_id}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar
                                    sx={{ 
                                      width: 32, 
                                      height: 32, 
                                      bgcolor: '#f1f2f4',
                                      color: '#202223',
                                      fontSize: '14px',
                                      fontWeight: 600
                                    }}
                                  >
                                    {userInitials}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                      {userFullName}
                                    </Typography>
                                  </Box>
                                </Box>

                                <Box>
                                  {userEmail && (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                      {userEmail}
                                    </Typography>
                                  )}
                                  {userContact && (
                                    <Typography variant="caption" color="text.secondary">
                                      {userContact}
                                    </Typography>
                                  )}
                                </Box>

                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
                                  {formatCurrency(creditLimit)}
                                </Typography>

                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#00783e' }}>
                                  {formatCurrency(balance)}
                                </Typography>

                                <Chip 
                                  label={`${utilization}%`}
                                  size="small"
                                  sx={{ 
                                    ...getUtilizationColor(utilization),
                                    fontWeight: 600,
                                    fontSize: '11px'
                                  }}
                                />

                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditCredit(user);
                                    }}
                                    size="small"
                                    disabled={isThisDeleting}
                                    sx={{ color: '#202223' }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(user);
                                    }}
                                    disabled={isThisDeleting || isDeleting}
                                    size="small"
                                  >
                                    {isThisDeleting ? <CircularProgress size={20} /> : <Delete fontSize="small" />}
                                  </IconButton>
                                </Box>
                              </>
                            ) : (
                              // Mobile View
                              <Stack spacing={2}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar
                                      sx={{ 
                                        width: 40, 
                                        height: 40, 
                                        bgcolor: '#f1f2f4',
                                        color: '#202223',
                                        fontWeight: 600
                                      }}
                                    >
                                      {userInitials}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                        {userFullName}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        ID: {user.user_id}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleExpand(user.user_id);
                                    }}
                                    sx={{ color: '#6d7175' }}
                                  >
                                    {expandedUserId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                  </IconButton>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#00783e' }}>
                                      {formatCurrency(balance)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Available Balance
                                    </Typography>
                                  </Box>
                                  <Chip 
                                    label={`${utilization}% Used`}
                                    size="small"
                                    sx={{ 
                                      ...getUtilizationColor(utilization),
                                      fontWeight: 600
                                    }}
                                  />
                                </Box>
                              </Stack>
                            )}
                          </Box>

                          {/* Expanded User Details */}
                          <Collapse in={expandedUserId === user.user_id} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 3, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>
                                  Credit Details
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    startIcon={<Edit />}
                                    onClick={() => handleEditCredit(user)}
                                    disabled={isThisDeleting}
                                    sx={{
                                      textTransform: 'none',
                                      borderRadius: '6px',
                                      fontWeight: 500,
                                    }}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={isThisDeleting ? <CircularProgress size={16} /> : <Delete />}
                                    onClick={() => handleDelete(user)}
                                    disabled={isThisDeleting || isDeleting}
                                    sx={{
                                      textTransform: 'none',
                                      borderRadius: '6px',
                                      fontWeight: 500,
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              </Box>
                              
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                  <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      Credit Limit
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#7b1fa2', mt: 0.5 }}>
                                      {formatCurrency(creditLimit)}
                                    </Typography>
                                  </Card>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      Available Balance
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#00783e', mt: 0.5 }}>
                                      {formatCurrency(balance)}
                                    </Typography>
                                  </Card>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      Utilized Amount
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#e65100', mt: 0.5 }}>
                                      {formatCurrency(parseFloat(creditLimit) - parseFloat(balance))}
                                    </Typography>
                                  </Card>
                                </Grid>
                              </Grid>

                              {(userEmail || userContact) && (
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white', mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#202223' }}>
                                    Contact Information
                                  </Typography>
                                  <Stack spacing={1.5}>
                                    {userEmail && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Email sx={{ color: '#6d7175', fontSize: 18 }} />
                                        <Typography variant="body2">
                                          {userEmail}
                                        </Typography>
                                      </Box>
                                    )}
                                    {userContact && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Phone sx={{ color: '#6d7175', fontSize: 18 }} />
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                          {userContact}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Stack>
                                </Card>
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Card>

              {totalPages > 1 && !isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handleChangePage}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: '8px',
                        fontWeight: 500,
                      },
                      '& .Mui-selected': {
                        bgcolor: '#202223 !important',
                        color: 'white',
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Credit Dialog */}
      <Dialog 
        open={formOpen} 
        onClose={handleFormClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px' }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {editingCredit ? 'Edit Credit Facility' : 'Add Credit Facility'}
            </Typography>
            <IconButton onClick={handleFormClose} size="small" disabled={isFormSubmitting}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={3}>
              {/* User Selection */}
              <FormControl fullWidth disabled={isFormSubmitting || Boolean(editingCredit)}>
                <InputLabel>Select User *</InputLabel>
                <Select
                  name="user_id"
                  value={formData.user_id.toString()}
                  onChange={handleSelectChange}
                  label="Select User *"
                  error={!!formErrors.user_id}
                  disabled={Boolean(editingCredit)}
                >
                  {editingCredit ? (
                    <MenuItem value={editingCredit.user_id}>
                      {`${getUserFullName(editingCredit.first_name, editingCredit.last_name)} (ID: ${editingCredit.user_id})`}
                    </MenuItem>
                  ) : (
                    availableUsers.map((user) => (
                      <MenuItem key={user.user_id} value={user.user_id}>
                        {`${user.name} (ID: ${user.user_id})`}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formErrors.user_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {formErrors.user_id}
                  </Typography>
                )}
              </FormControl>

              {/* Credit Limit */}
              <TextField
                name="credit_limit"
                label="Credit Limit *"
                type="number"
                value={formData.credit_limit}
                onChange={handleInputChange}
                error={!!formErrors.credit_limit}
                helperText={formErrors.credit_limit || 'Maximum credit amount allowed'}
                fullWidth
                required
                disabled={isFormSubmitting}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  inputProps: { min: 0, step: 0.01 }
                }}
              />

              {/* Balance */}
              <TextField
                name="balance"
                label="Available Balance *"
                type="number"
                value={formData.balance}
                onChange={handleInputChange}
                error={!!formErrors.balance}
                helperText={formErrors.balance || 'Current available credit balance'}
                fullWidth
                required
                disabled={isFormSubmitting}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  inputProps: { min: 0, step: 0.01 }
                }}
              />

              {editingCredit && (
                <Alert severity="info" sx={{ borderRadius: '8px' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Current Status
                  </Typography>
                  <Typography variant="body2">
                    Credit Limit: {formatCurrency(getCreditLimit(editingCredit))}
                  </Typography>
                  <Typography variant="body2">
                    Available Balance: {formatCurrency(getBalance(editingCredit))}
                  </Typography>
                  <Typography variant="body2">
                    Utilized: {formatCurrency(parseFloat(getCreditLimit(editingCredit)) - parseFloat(getBalance(editingCredit)))}
                  </Typography>
                </Alert>
              )}

              {!editingCredit && formData.credit_limit && (
                <Alert severity="info" sx={{ borderRadius: '8px' }}>
                  <Typography variant="body2">
                    💡 Balance is typically set equal to credit limit initially, then decreases as the user makes purchases.
                  </Typography>
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
            <Button 
              onClick={handleFormClose} 
              disabled={isFormSubmitting}
              variant="outlined"
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isFormSubmitting || !formData.user_id || !formData.credit_limit || formData.balance === ''}
              sx={{
                textTransform: 'none',
                bgcolor: '#202223',
                borderRadius: '8px',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#000000',
                }
              }}
            >
              {isFormSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  {editingCredit ? 'Updating...' : 'Setting...'}
                </Box>
              ) : (
                editingCredit ? 'Update Credit' : 'Set Credit'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SetCredit;